<template>
    <div>
        <div class="grid">
            <div v-for="template in templates" :key="template.key" :class="{ inactive: !value[template.key]?.active }">

                <label class="d-flex">
                    <b-checkbox switch @input="updateValue(template.key, 'active', $event)" size="sm"
                        :checked="value[template.key]?.active" :disabled="disabled" />
                    {{ template.label }}
                </label>
                <b-form-group :disabled="disabled">
                    <label>
                        Template Id
                        <HelpPopup markdown="The SendGrid dynamic template ID for this email type" />
                    </label>
                    <b-form-input :value="value[template.key]?.templateId"
                        @input="updateValue(template.key, 'templateId', $event)" />
                </b-form-group>
                <b-form-group :disabled="disabled">
                    <label>
                        From Email Address
                        <HelpPopup markdown="The sender email address that will appear in the 'From' field" />
                    </label>
                    <b-form-input :value="value[template.key]?.fromEmail"
                        @input="updateValue(template.key, 'fromEmail', $event)" />
                </b-form-group>
                <b-form-group :disabled="disabled">
                    <label>
                        ASM Group
                        <HelpPopup markdown="SendGrid Advanced Suppression Manager (ASM) Group ID. This allows recipients to manage their subscription preferences and unsubscribe from specific email groups. Create groups in SendGrid's Unsubscribe Groups settings." />
                    </label>
                    <b-form-input :value="value[template.key]?.asmGroup"
                        @input="updateValue(template.key, 'asmGroup', $event)"
                        type="number" />
                </b-form-group>

            </div>
        </div>

    </div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-property-decorator';
import { EmailTemplate } from "@/models/EmailTemplates";
import HelpPopup from '@/components/HelpPopup.vue';

interface TemplateValue {
    active?: boolean;
    templateId?: string;
    fromEmail?: string;
    asmGroup?: number;
}

interface TemplateDefinition {
    label: string;
    key: string;
}

@Component({
    name: 'EmailTemplatesField',
    components: {
        HelpPopup
    }
})
export default class EmailTemplatesField extends Vue {

    @Prop({ default: () => ({}) }) value!: Record<string, EmailTemplate>;
    @Prop({ default: () => ({}) }) previewData!: Record<string, string>;
    @Prop({ default: () => [] }) hideTemplates!: string[];
    @Prop({
        type: Boolean,
        default: false,
    }) disabled!: boolean;

    // Methods
    updateValue(templateName: string, fieldName: keyof TemplateValue, value: string | boolean | number): void {
        const newValue = { ...this.value };
        if (!newValue[templateName]) {
            newValue[templateName] = {} as EmailTemplate;
        }
        // Type assertion needed because fieldName determines value type
        if (fieldName === 'active') {
            newValue[templateName][fieldName] = value as boolean;
        } else if (fieldName === 'templateId' || fieldName === 'fromEmail') {
            newValue[templateName][fieldName] = value as string;
        } else if (fieldName === 'asmGroup') {
            newValue[templateName][fieldName] = value ? value as number : undefined;
        }

        this.$emit('input', newValue);
    }

    get templates(): TemplateDefinition[] {
        const baseTemplates: TemplateDefinition[] = [
            {
                label: "New Prescription",
                key: "newPrescription",
            },
            {
                label: "Refill",
                key: "refill",
            },
            {
                label: "MedSync Reminder",
                key: "medSyncReminder",
            },
        ];

        return baseTemplates.filter(i => !this.hideTemplates?.includes(i.key));
    }
}
</script>

<style scoped>
.grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 300px), 1fr));
    gap: 15px;
    margin-top: 10px;
}

@media screen and (min-width: 1200px) {
    .grid {
        grid-template-columns: repeat(3, 1fr);
    }
}

.grid > div >>> textarea {
    transition: all .2s;
    height: 100px !important;
    max-height: 100px !important;
    line-height: 1.45 !important;
}

.grid > div.inactive >>> textarea {
    opacity: .6;
}

.text-message-template .tokens code {
    display: inline-block;
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    padding: 2px 5px;
    margin: 2px;
    border-radius: 3px;
    cursor: pointer;
    font-family: monospace;
}

.text-message-template .tokens code:hover {
    background-color: #e9ecef;
}

.text-message-template h6 {
    margin-top: 10px;
    margin-bottom: 5px;
    font-size: 0.9rem;
    font-weight: bold;
}

.text-message-template h6:first-child {
    margin-top: 0;
}
</style>
